import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchCasaMapaAdminVendors } from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import {
  CASAMAPA_ADMIN_VENDOR_SECTIONS,
  casamapaAdminVendorHubPath,
  casamapaAdminVendorManagePath,
} from "../../../constants/casamapaAdminVendorPaths";
import { casamapaVendorPublicProfilePath } from "../../../constants/casamapaVendorWorkspacePaths";
import { casamapaPublicVendorDirectoryHref } from "../../../constants/casamapaVendorWorkspacePaths";
import { useLocale } from "../../../intl";
import "../../../styles/partnerPortalDashboard.css";
import "../../../styles/partnerVendorsPage.css";

const LIFECYCLE_FILTERS = ["", "active", "suspended", "pending_verification", "discovered"];

const PUBLICATION_CHIP_FILTERS = ["", "published", "draft", "ready_for_review"];

function publicationBadgeClass(status) {
  if (status === "published") return "published";
  if (status === "ready_for_review") return "review";
  if (status === "unpublished" || status === "suspended") return "suspended";
  return "draft";
}

function lifecycleBadgeClass(status) {
  if (status === "active") return "active";
  if (status === "pending_verification") return "pending";
  if (status === "suspended") return "suspended";
  return "discovered";
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale);
}

function KpiSkeleton() {
  return (
    <div className="partner-kpi-grid" aria-hidden="true">
      {[1, 2, 3, 4].map((id) => (
        <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
      ))}
    </div>
  );
}

function VendorsSkeleton() {
  return (
    <div className="partner-vendors-page__skeleton-grid" aria-hidden="true">
      {[1, 2, 3, 4, 5, 6].map((id) => (
        <div key={id} className="partner-vendors-page__skeleton-card" />
      ))}
    </div>
  );
}

function OpsSkeleton() {
  return (
    <div className="partner-vendors-page__ops-groups" aria-hidden="true">
      {[1, 2, 3, 4].map((id) => (
        <div key={id} className="partner-skeleton-block partner-vendors-page__ops-skeleton" />
      ))}
    </div>
  );
}

function OperationLink({ to, icon, title, hint, count, attention = false, meta }) {
  if (!to) return null;

  return (
    <li>
      <Link
        className={`partner-vendors-page__ops-link${attention ? " partner-vendors-page__ops-link--attention" : ""}`}
        to={to}
      >
        <span className="partner-vendors-page__ops-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="partner-vendors-page__ops-copy">
          <strong>
            {title}
            {count > 0 ? (
              <span
                className={`partner-vendors-page__ops-count${attention ? " partner-vendors-page__ops-count--warn" : ""}`}
              >
                {count}
              </span>
            ) : null}
          </strong>
          <span>{hint}</span>
          {meta ? <span className="partner-vendors-page__ops-meta">{meta}</span> : null}
        </span>
      </Link>
    </li>
  );
}

function VendorCard({ vendor, t, locale, clientViewId, manageLabel, publicLabel }) {
  const publicationKey = vendor.publicationStatus || "draft";
  const lifecycleKey = vendor.lifecycleStatus || "discovered";

  return (
    <article className="partner-vendor-card">
      <div className="partner-vendor-card__head">
        <span className="partner-vendor-card__mark" aria-hidden="true">
          {String(vendor.name || "?").slice(0, 1)}
        </span>
        <div className="partner-vendor-card__identity">
          <h3>{vendor.name}</h3>
          <p className="partner-vendor-card__slug">{vendor.slug}</p>
        </div>
        <div className="partner-vendor-card__badges">
          <span
            className={`partner-vendor-card__badge partner-vendor-card__badge--${publicationBadgeClass(publicationKey)}`}
          >
            {t(`realEstate.casaMapa.admin.vendors.publicationStatus.${publicationKey}`, {}, publicationKey)}
          </span>
          <span
            className={`partner-vendor-card__badge partner-vendor-card__badge--${lifecycleBadgeClass(lifecycleKey)}`}
          >
            {t(`realEstate.casaMapa.admin.vendors.lifecycleStatus.${lifecycleKey}`, {}, lifecycleKey)}
          </span>
        </div>
      </div>

      <dl className="partner-vendor-card__meta">
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.category")}</dt>
          <dd>{vendor.category || "—"}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.municipality")}</dt>
          <dd>{vendor.municipality || "—"}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.updated")}</dt>
          <dd>{formatDate(vendor.updatedAt, locale)}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.completeness")}</dt>
          <dd>{vendor.profileCompleteness != null ? `${vendor.profileCompleteness}%` : "—"}</dd>
        </div>
      </dl>

      <div className="partner-vendor-card__actions">
        {clientViewId ? (
          <Link
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            to={casamapaAdminVendorManagePath(clientViewId, vendor.id)}
          >
            {manageLabel}
          </Link>
        ) : null}
        <a
          className="partner-vendor-card__link"
          href={casamapaVendorPublicProfilePath(vendor.slug)}
          target="_blank"
          rel="noreferrer"
        >
          {publicLabel}
        </a>
      </div>
    </article>
  );
}

export default function CasaMapaAdminVendorsPage() {
  const { resolvedViewId } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState(null);
  const [clientViewId, setClientViewId] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchDraft, setSearchDraft] = useState(searchParams.get("q") || "");

  const search = searchParams.get("q") || "";
  const publicationStatus = searchParams.get("publicationStatus") || "";
  const lifecycleStatus = searchParams.get("lifecycleStatus") || "";
  const page = Math.max(Number(searchParams.get("page") || 1), 1);
  const pageSize = 50;

  const loadVendors = useCallback(async () => {
    if (!resolvedViewId) return;
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaAdminVendors(resolvedViewId, {
        search: search.trim() || undefined,
        publicationStatus: publicationStatus || undefined,
        lifecycleStatus: lifecycleStatus || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setVendors(result.vendors || []);
      setSummary(result.vendorDirectory || null);
      setOperations(result.operationalSummary || null);
      setClientViewId(result.clientViewId || null);
      setPagination(result.pagination || { total: 0, limit: pageSize, offset: 0, hasMore: false });
    } catch (err) {
      if (err.status === 403) {
        setError(t("realEstate.casaMapa.admin.errors.noCasaMapaAccess"));
      } else if (err.status === 401) {
        setError(t("realEstate.casaMapa.admin.errors.partnerAuthRequired"));
      } else {
        setError(err.message || t("realEstate.casaMapa.admin.errors.loadVendors"));
      }
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [resolvedViewId, lifecycleStatus, page, publicationStatus, search, t]);

  useEffect(() => {
    if (resolvedViewId) {
      loadVendors();
    }
  }, [resolvedViewId, loadVendors]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const trimmed = searchDraft.trim();
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      next.delete("page");
      setSearchParams(next, { replace: true });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft, searchParams, setSearchParams]);

  const workspaceHref = useMemo(
    () => (resolvedViewId ? casamapaAdminVendorHubPath(resolvedViewId) : null),
    [resolvedViewId]
  );

  const ws = useCallback(
    (section) => (resolvedViewId ? casamapaAdminVendorHubPath(resolvedViewId, section) : null),
    [resolvedViewId]
  );

  const operationGroups = useMemo(() => {
    if (!resolvedViewId) return [];

    const ops = operations || {};
    const dir = summary || {};
    const discoveryMeta = [
      ops.lastCollectionCompletedAt
        ? t("realEstate.casaMapa.admin.vendors.pipeline.lastRun", {
            date: formatDate(ops.lastCollectionCompletedAt, locale),
          })
        : null,
      ops.recentFailedCollectionRuns > 0
        ? t("realEstate.casaMapa.admin.vendors.pipeline.failedRuns", {
            count: ops.recentFailedCollectionRuns,
          })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return [
      {
        id: "intake",
        labelKey: "realEstate.casaMapa.admin.vendors.pipeline.groups.intake",
        items: [
          {
            key: "candidates",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.candidates),
            icon: "📥",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.candidates",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.candidatesHint",
            count: ops.candidateReview || dir.pendingCandidates || 0,
            attention: (ops.candidateReview || dir.pendingCandidates || 0) > 0,
          },
          {
            key: "imports",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.imports),
            icon: "📂",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.imports",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.importsHint",
            count: ops.activeImportJobs || 0,
            attention: (ops.activeImportJobs || 0) > 0,
          },
          {
            key: "discovery",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.collectionRuns),
            icon: "🔍",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.discovery",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.discoveryHint",
            count: ops.activeCollectionRuns || 0,
            attention: (ops.recentFailedCollectionRuns || 0) > 0,
            meta: discoveryMeta || null,
          },
          {
            key: "collectionPlans",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.collectionPlans),
            icon: "🗓️",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.collectionPlans",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.collectionPlansHint",
          },
        ],
      },
      {
        id: "review",
        labelKey: "realEstate.casaMapa.admin.vendors.pipeline.groups.review",
        items: [
          {
            key: "duplicates",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.duplicates),
            icon: "🧩",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.duplicates",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.duplicatesHint",
            count: ops.duplicateReview || 0,
            attention: (ops.duplicateReview || 0) > 0,
          },
          {
            key: "verification",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.verification),
            icon: "✅",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.verification",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.verificationHint",
            count: ops.verification || 0,
            attention: (ops.verification || 0) > 0,
          },
          {
            key: "credentials",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.credentials),
            icon: "🪪",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.credentials",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.credentialsHint",
            count: ops.credentials || 0,
            attention: (ops.credentials || 0) > 0,
          },
          {
            key: "allVendors",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.vendors),
            icon: "🏠",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.allVendors",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.allVendorsHint",
            count: dir.totalVendors || 0,
          },
          {
            key: "createVendor",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.vendorCreate),
            icon: "➕",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.createVendor",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.createVendorHint",
          },
        ],
      },
      {
        id: "requests",
        labelKey: "realEstate.casaMapa.admin.vendors.pipeline.groups.requests",
        items: [
          {
            key: "claims",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.claims),
            icon: "📋",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.claims",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.claimsHint",
            count: ops.claims || 0,
            attention: (ops.claims || 0) > 0,
          },
          {
            key: "privacy",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.privacy),
            icon: "🔒",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.privacy",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.privacyHint",
            count: ops.privacyRequests || 0,
            attention: (ops.privacyRequests || 0) > 0,
          },
        ],
      },
      {
        id: "directory",
        labelKey: "realEstate.casaMapa.admin.vendors.pipeline.groups.directory",
        items: [
          {
            key: "sources",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.sources),
            icon: "🌐",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.sources",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.sourcesHint",
            count: ops.activeSources || 0,
          },
          {
            key: "ranking",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.ranking),
            icon: "📊",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.ranking",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.rankingHint",
          },
          {
            key: "analytics",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.analytics),
            icon: "📈",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.analytics",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.analyticsHint",
          },
          {
            key: "maintenance",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.maintenance),
            icon: "🛠️",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.maintenance",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.maintenanceHint",
          },
          {
            key: "pilot",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.pilot),
            icon: "🚀",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.pilot",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.pilotHint",
          },
          {
            key: "expansion",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.expansion),
            icon: "🧭",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.expansion",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.expansionHint",
          },
          {
            key: "marketplace",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.marketplace),
            icon: "🛒",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.marketplace",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.marketplaceHint",
          },
          {
            key: "settings",
            to: ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.settings),
            icon: "⚙️",
            titleKey: "realEstate.casaMapa.admin.vendors.pipeline.settings",
            hintKey: "realEstate.casaMapa.admin.vendors.pipeline.settingsHint",
          },
        ],
      },
    ];
  }, [locale, operations, resolvedViewId, summary, t, ws]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pageSize));
  const hasActiveFilters = Boolean(searchDraft.trim() || publicationStatus || lifecycleStatus);

  const manageViewId = clientViewId || resolvedViewId;

  if (!resolvedViewId) {
    return (
      <section className="partner-vendors-page partner-vendors-page--dashboard">
        <p className="muted">{t("realEstate.casaMapa.admin.errors.noViewMessage")}</p>
      </section>
    );
  }

  return (
    <section className="partner-vendors-page partner-vendors-page--dashboard">
      <header className="partner-vendors-page__hero partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            {t("realEstate.casaMapa.admin.portalTitle")} · {t("realEstate.casaMapa.admin.internalWorkspaceLabel")}
          </p>
          <h2>{t("realEstate.casaMapa.admin.vendors.title")}</h2>
          <p className="partner-dashboard-hero__intro">{t("realEstate.casaMapa.admin.vendors.subtitle")}</p>
          <p className="partner-vendors-page__workspace-hint">{t("realEstate.casaMapa.admin.vendors.workspaceHint")}</p>
        </div>
        {workspaceHref ? (
          <div className="partner-dashboard-hero__actions">
            <a
              className="partner-dashboard-btn"
              href={casamapaPublicVendorDirectoryHref(locale)}
              target="_blank"
              rel="noreferrer"
            >
              {t("realEstate.casaMapa.admin.vendors.viewPublicDirectory")}
            </a>
            <Link
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              to={ws(CASAMAPA_ADMIN_VENDOR_SECTIONS.vendorCreate)}
            >
              {t("realEstate.casaMapa.admin.vendors.addToPublicSite")}
            </Link>
            <Link className="partner-dashboard-btn" to={workspaceHref}>
              {t("realEstate.casaMapa.admin.vendors.openWorkspace")}
            </Link>
          </div>
        ) : null}
      </header>

      {loading && !summary ? (
        <KpiSkeleton />
      ) : summary ? (
        <div className="partner-kpi-grid" aria-label={t("realEstate.casaMapa.admin.dashboard.kpiLabel")}>
          <article className="partner-kpi-card partner-kpi-card--green">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              ✅
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.vendors.kpi.published")}</span>
            <strong className="partner-kpi-card__value">{summary.publishedVendors ?? 0}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--blue">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              🏠
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.vendors.kpi.total")}</span>
            <strong className="partner-kpi-card__value">{summary.totalVendors ?? 0}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--amber">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              📥
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.vendors.kpi.candidates")}</span>
            <strong className="partner-kpi-card__value">{summary.pendingCandidates ?? 0}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--amber">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              👀
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.vendors.kpi.readyForReview")}</span>
            <strong className="partner-kpi-card__value">{summary.readyForReviewVendors ?? 0}</strong>
          </article>
        </div>
      ) : null}

      <section className="partner-vendors-page__ops-dashboard" aria-label={t("realEstate.casaMapa.admin.vendors.pipeline.title")}>
        <div className="partner-vendors-page__ops-head">
          <h2>{t("realEstate.casaMapa.admin.vendors.pipeline.title")}</h2>
          <p>{t("realEstate.casaMapa.admin.vendors.pipeline.subtitle")}</p>
        </div>

        {loading && !operations ? (
          <OpsSkeleton />
        ) : (
          <div className="partner-vendors-page__ops-groups">
            {operationGroups.map((group) => (
              <div key={group.id} className="partner-vendors-page__ops-column">
                <p className="partner-vendors-page__ops-group-label">{t(group.labelKey)}</p>
                <ul className="partner-vendors-page__ops-list">
                  {group.items.map((item) => (
                    <OperationLink
                      key={item.key}
                      to={item.to}
                      icon={item.icon}
                      title={t(item.titleKey)}
                      hint={t(item.hintKey)}
                      count={item.count}
                      attention={item.attention}
                      meta={item.meta}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="partner-vendors-page__directory" aria-label={t("realEstate.casaMapa.admin.vendors.directory.title")}>
        <div className="partner-vendors-page__section-head">
          <h2>{t("realEstate.casaMapa.admin.vendors.directory.title")}</h2>
          <p>{t("realEstate.casaMapa.admin.vendors.directory.subtitle")}</p>
        </div>

        <div className="partner-vendors-page__main">

          <div className="partner-vendors-page__toolbar">
            <div className="partner-vendors-page__search">
              <span className="partner-vendors-page__search-icon" aria-hidden="true">
                ⌕
              </span>
              <input
                type="search"
                placeholder={t("realEstate.casaMapa.admin.vendors.searchPlaceholder")}
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                aria-label={t("realEstate.casaMapa.admin.vendors.searchPlaceholder")}
              />
            </div>
            <select
              className="partner-vendors-page__select"
              value={lifecycleStatus}
              onChange={(event) => updateFilter("lifecycleStatus", event.target.value)}
              aria-label={t("realEstate.casaMapa.admin.vendors.filters.lifecycle")}
            >
              {LIFECYCLE_FILTERS.map((value) => (
                <option key={value || "all"} value={value}>
                  {value
                    ? t(`realEstate.casaMapa.admin.vendors.lifecycleStatus.${value}`)
                    : t("realEstate.casaMapa.admin.vendors.filters.allLifecycle")}
                </option>
              ))}
            </select>
            {hasActiveFilters ? (
              <button
                type="button"
                className="partner-vendors-page__toolbar-btn"
                onClick={() => {
                  setSearchDraft("");
                  const next = new URLSearchParams();
                  setSearchParams(next, { replace: true });
                }}
              >
                {t("realEstate.casaMapa.admin.listings.clearFilter")}
              </button>
            ) : null}
            <button type="button" className="partner-vendors-page__toolbar-btn" onClick={loadVendors}>
              {t("realEstate.casaMapa.admin.refresh")}
            </button>
          </div>

          <div className="partner-vendors-page__chips" role="group" aria-label={t("realEstate.casaMapa.admin.vendors.filters.publication")}>
            {PUBLICATION_CHIP_FILTERS.map((value) => (
              <button
                key={value || "all"}
                type="button"
                className={`partner-vendors-page__chip${publicationStatus === value ? " is-active" : ""}`}
                onClick={() => updateFilter("publicationStatus", value)}
              >
                {value
                  ? t(`realEstate.casaMapa.admin.vendors.publicationStatus.${value}`)
                  : t("realEstate.casaMapa.admin.vendors.filters.allPublication")}
              </button>
            ))}
          </div>

          {error ? (
            <div className="partner-vendors-page__error" role="alert">
              <p>{error}</p>
              <button type="button" className="partner-dashboard-btn" onClick={loadVendors}>
                {t("realEstate.casaMapa.admin.vendors.retry")}
              </button>
            </div>
          ) : null}

          {loading ? <VendorsSkeleton /> : null}

          {!loading && vendors.length ? (
            <>
              <p className="partner-vendors-page__results">
                {t("realEstate.casaMapa.admin.vendors.pagination.page", {
                  page,
                  total: totalPages,
                  count: pagination.total,
                })}
              </p>
              <div className="partner-vendors-page__grid">
                {vendors.map((vendor) => (
                  <VendorCard
                    key={vendor.id}
                    vendor={vendor}
                    t={t}
                    locale={locale}
                    clientViewId={manageViewId}
                    manageLabel={t("realEstate.casaMapa.admin.vendors.manageVendor")}
                    publicLabel={t("realEstate.casaMapa.admin.viewPublic")}
                  />
                ))}
              </div>

              {pagination.total > pageSize ? (
                <div className="partner-vendors-page__pagination">
                  <button
                    type="button"
                    className="partner-dashboard-btn"
                    disabled={page <= 1}
                    onClick={() => updateFilter("page", String(page - 1))}
                  >
                    {t("realEstate.casaMapa.admin.vendors.pagination.previous")}
                  </button>
                  <span className="muted">
                    {t("realEstate.casaMapa.admin.vendors.pagination.page", {
                      page,
                      total: totalPages,
                      count: pagination.total,
                    })}
                  </span>
                  <button
                    type="button"
                    className="partner-dashboard-btn"
                    disabled={!pagination.hasMore}
                    onClick={() => updateFilter("page", String(page + 1))}
                  >
                    {t("realEstate.casaMapa.admin.vendors.pagination.next")}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {!loading && !error && !vendors.length ? (
            <div className="partner-vendors-page__empty">
              <span aria-hidden="true">🏠</span>
              <h3>{t("realEstate.casaMapa.admin.vendors.empty")}</h3>
              {hasActiveFilters ? (
                <p>{t("realEstate.casaMapa.admin.vendors.searchPlaceholder")}</p>
              ) : null}
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  onClick={() => {
                    setSearchDraft("");
                    setSearchParams(new URLSearchParams(), { replace: true });
                  }}
                >
                  {t("realEstate.casaMapa.admin.listings.clearFilter")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </section>
  );
}
